"use client";

import { useRouter } from "next/navigation";
import { type ReactNode, useEffect, useRef, useTransition } from "react";
import { hasSysId } from "@/services/contentful/assemblies/page/adapter/collect-server-blocks";

const DEBOUNCE_MS = 1500;

export function useServerBlockRefresh<T>(
  updatedPage: T,
  getItems: (page: T) => unknown[],
  serverSlots: Map<string, ReactNode>
): void {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const prevPageRef = useRef<T>(updatedPage);
  const isPendingRef = useRef(false);
  isPendingRef.current = isPending;

  // serverSlots is a new Map on every server render; including it would
  // cause this effect to fire on every refresh, not just on editor changes.
  // router.refresh and startTransition are stable references — safe to omit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — only re-run on editor updates
  useEffect(() => {
    if (updatedPage === prevPageRef.current) {
      return;
    }

    if (isPendingRef.current) {
      prevPageRef.current = updatedPage;
      return;
    }

    const currItems = getItems(updatedPage);
    const prevItems = getItems(prevPageRef.current);

    const changed = new Set(
      currItems
        .filter((curr): curr is { sys: { id: string } } => {
          if (!hasSysId(curr)) {
            return false;
          }

          if (!serverSlots.has(curr.sys.id)) {
            return false;
          }

          const prev = prevItems.find(
            (p) => hasSysId(p) && p.sys.id === curr.sys.id
          );

          return prev !== curr;
        })
        .map((curr) => curr.sys.id)
    );

    prevPageRef.current = updatedPage;

    if (changed.size === 0) {
      return;
    }

    const timer = setTimeout(() => {
      if (!isPendingRef.current) {
        startTransition(() => router.refresh());
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [updatedPage]);
}
